// Outil gratuit d'estimation : appels à la route publique /v0/public/estimation (sans compte, sans jeton).
// Les réponses ne quittent le navigateur que pour le calcul ; rien n'est gardé ni dans l'adresse, ni dans un stockage.
import type { components } from '../api/schema';
import { resoudreDefi } from './defiTravail';

type S = components['schemas'];
export type Questionnaire = S['QuestionnaireEstimation'];
export type QuestionEstimation = S['QuestionEstimation'];
export type ResultatEstimation = S['ResultatEstimation'];
export type Reponses = S['Reponses'];

/** Une réponse en cours de saisie : une valeur, ou une liste pour les questions à choix multiples. */
export type Saisie = Record<string, string | string[] | undefined>;

export class ErreurEstimation extends Error {
  constructor(
    readonly statut: number,
    readonly code: string,
    message: string,
    readonly attente?: number,
  ) {
    super(message);
  }
}

/** Message dit à la personne pour chaque refus de l'API (jamais le texte brut du serveur). */
export function messageErreur(e: unknown): string {
  if (!(e instanceof ErreurEstimation)) return 'Le service ne répond pas. Vérifiez votre connexion et réessayez.';
  switch (e.code) {
    case 'trop_de_demandes':
      return `Trop de demandes depuis votre connexion. Réessayez${e.attente ? ` dans ${Math.ceil(e.attente / 60)} min` : ' plus tard'}.`;
    case 'occupe':
    case 'calcul_trop_long':
    case 'indisponible':
      return 'Le service est très sollicité. Réessayez dans quelques secondes.';
    case 'defi_invalide':
      return 'La vérification anti-abus a expiré. Relancez le calcul.';
    case 'courriel_non_professionnel':
      return 'Indiquez votre courriel professionnel : les messageries grand public et jetables sont refusées.';
    case 'courriel_indisponible':
    case 'recontact_indisponible':
      return 'L’envoi par courriel est momentanément indisponible.';
    case 'reponses_invalides':
      return 'Une réponse n’a pas été reconnue. Rechargez la page et réessayez.';
    case 'lien_invalide':
      return 'Ce lien de confirmation a expiré ou a déjà servi. Refaites une estimation pour en recevoir un nouveau.';
    case 'lecture_trop_lente':
      return 'La connexion est trop lente. Réessayez dans un instant.';
    default:
      return 'Le calcul n’a pas abouti. Réessayez dans un instant.';
  }
}

type Recuperer = typeof fetch;

async function appel<T>(recuperer: Recuperer, chemin: string, init?: RequestInit): Promise<T> {
  let r: Response;
  try {
    r = await recuperer(chemin, { ...init, credentials: 'omit', cache: 'no-store', referrerPolicy: 'no-referrer' });
  } catch {
    throw new ErreurEstimation(0, 'reseau', 'réseau');
  }
  if (!r.ok) {
    let code = 'erreur';
    try {
      code = ((await r.json()) as { code?: string }).code ?? code;
    } catch {
      /* corps illisible */
    }
    const attente = Number(r.headers.get('Retry-After'));
    throw new ErreurEstimation(r.status, code, code, Number.isFinite(attente) && attente > 0 ? attente : undefined);
  }
  return (await r.json()) as T;
}

/** Les questions fermées ont toutes une réponse (les listes peuvent rester vides). */
export function saisieComplete(q: Questionnaire, s: Saisie): boolean {
  return q.questions.every((x) => (x.multiple ? true : typeof s[x.id] === 'string'));
}

export function nombreRepondues(q: Questionnaire, s: Saisie): number {
  return q.questions.filter((x) => (x.multiple ? Array.isArray(s[x.id]) : typeof s[x.id] === 'string')).length;
}

/** Le corps envoyé : seulement les valeurs des options du questionnaire, dans l'ordre des questions. */
export function versReponses(q: Questionnaire, s: Saisie): Reponses {
  const r: Record<string, string | string[]> = {};
  for (const x of q.questions) {
    const permises = new Set(x.options.map((o) => o.valeur));
    const v = s[x.id];
    if (x.multiple) r[x.id] = (Array.isArray(v) ? v : []).filter((y) => permises.has(y));
    else if (typeof v === 'string' && permises.has(v)) r[x.id] = v;
  }
  return r as unknown as Reponses;
}

export class ClientEstimation {
  constructor(
    private readonly recuperer: Recuperer = (...a) => fetch(...a),
    private readonly resoudre: typeof resoudreDefi = resoudreDefi,
  ) {}

  questionnaire(): Promise<Questionnaire> {
    return appel(this.recuperer, '/v0/public/estimation/questionnaire');
  }

  private async preuve(signal?: AbortSignal): Promise<{ defi: string; solution: string }> {
    const d = await appel<{ defi: string; bits: number }>(this.recuperer, '/v0/public/estimation/defi');
    return { defi: d.defi, solution: await this.resoudre(d.defi, d.bits, { signal }) };
  }

  async estimer(reponses: Reponses, signal?: AbortSignal): Promise<ResultatEstimation> {
    const corps = JSON.stringify({ reponses, ...(await this.preuve(signal)) });
    return appel(this.recuperer, '/v0/public/estimation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: corps,
      signal,
    });
  }

  /** Confirme une demande de contact avec le jeton du lien reçu par courriel (dans le corps, jamais dans une adresse
   * envoyée au serveur) : seule cette confirmation transmet l'adresse à Ekera. */
  confirmer(jeton: string): Promise<{ recontact: string }> {
    return appel(this.recuperer, '/v0/public/estimation/recontact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ jeton }),
    });
  }

  async envoyer(reponses: Reponses, courriel: string, recontact: boolean): Promise<{ recontact: boolean }> {
    const corps = JSON.stringify({ reponses, ...(await this.preuve()), courriel, consentement_envoi: true, recontact });
    return appel(this.recuperer, '/v0/public/estimation/courriel', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: corps,
    });
  }
}

/** Le jeton d'un lien de confirmation (#/estimation/contact?jeton=…), ou null : forme stricte, rien d'autre n'est lu. */
export function jetonDuFragment(fragment: string): string | null {
  const m = /^#\/estimation\/contact\?jeton=([A-Za-z0-9_-]{1,700}\.[A-Za-z0-9_-]{43})$/.exec(fragment);
  return m ? m[1]! : null;
}

export const LIBELLE_ETAT_NIS2: Record<string, string> = { tenue: 'Tenue', partielle: 'Partielle', en_ecart: 'En écart' };
